import { useState } from 'react';
import Header from './components/Header.jsx';
import Uploader from './components/Uploader.jsx';
import ReviewProgress from './components/ReviewProgress.jsx';
import SamplePicker from './components/SamplePicker.jsx';
import PasswordGate from './components/PasswordGate.jsx';
import DownloadButtons from './components/DownloadButtons.jsx';
import HistoryView from './components/HistoryView.jsx';
import Pricing from './components/Pricing.jsx';
import Landing from './components/Landing.jsx';
import { runReview, PIPELINE_STEPS } from './lib/orchestrator.js';
import { clearAuthToken, getAuthToken } from './lib/api.js';
import { saveReview, buildReviewPayload } from './lib/history.js';
import { readCheckoutReturnFromUrl, verifyCheckout } from './lib/billing.js';
import { formatUsd } from './lib/cost.js';
import { useEffect } from 'react';

const STATE = {
  IDLE: 'idle', REVIEWING: 'reviewing', DONE: 'done', ERROR: 'error',
  HISTORY: 'history', PRICING: 'pricing',
};

export default function App() {
  const [authed, setAuthed] = useState(() => Boolean(getAuthToken()));
  // When unauthenticated, show the marketing Landing first; only flip to the
  // PasswordGate once the visitor clicks Sign in.
  const [showGate, setShowGate] = useState(false);
  const [phase, setPhase] = useState(STATE.IDLE);
  const [extracted, setExtracted] = useState(null); // { text, fileName, pages, fileType, looksScanned }
  const [progress, setProgress] = useState({}); // { stepId: { status, durationMs, error } }
  const [accumulated, setAccumulated] = useState({}); // partial outputs from completed steps
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [historySave, setHistorySave] = useState(null); // { status: 'saving'|'ok'|'unconfigured'|'error', message?, url? }
  const [checkoutToast, setCheckoutToast] = useState(null); // { kind: 'success'|'cancel'|'error', message?, email?, amount? }

  // After redirect from Stripe Checkout, surface a toast and verify if applicable.
  useEffect(() => {
    if (!authed) return;
    const ret = readCheckoutReturnFromUrl();
    if (!ret) return;
    if (ret.status === 'cancel') {
      setCheckoutToast({ kind: 'cancel', message: 'Checkout cancelled.' });
      return;
    }
    if (ret.status === 'success' && ret.sessionId) {
      setCheckoutToast({ kind: 'success', message: 'Verifying payment…' });
      verifyCheckout(ret.sessionId)
        .then((data) => {
          if (data.paid) {
            setCheckoutToast({
              kind: 'success', email: data.customerEmail,
              amount: `${(data.amountTotal / 100).toFixed(2)} ${(data.currency || '').toUpperCase()}`,
            });
          } else {
            setCheckoutToast({ kind: 'error', message: `Payment status: ${data.paymentStatus}` });
          }
        })
        .catch((err) => setCheckoutToast({ kind: 'error', message: err?.message || 'Verify failed' }));
    }
  }, [authed]);

  if (!authed) {
    if (!showGate) return <Landing onSignIn={() => setShowGate(true)} />;
    return (
      <PasswordGate
        onUnlock={() => { setAuthed(true); setShowGate(false); }}
        onBack={() => setShowGate(false)}
      />
    );
  }

  function logout() {
    clearAuthToken();
    setAuthed(false);
    setPhase(STATE.IDLE);
    setExtracted(null);
    setProgress({});
    setAccumulated({});
    setResult(null);
    setError(null);
  }

  async function start(extractedFile, previous = {}) {
    setExtracted(extractedFile);
    setError(null);
    setPhase(STATE.REVIEWING);

    // Resume case: keep prior accumulated; mark cached steps as 'skipped'.
    if (Object.keys(previous).length === 0) {
      setProgress({});
      setAccumulated({});
    }

    try {
      const out = await runReview(extractedFile, {
        onProgress: (update) => {
          setProgress((p) => ({
            ...p,
            [update.stepId]: {
              status: update.status,
              durationMs: update.durationMs,
              error: update.error,
            },
          }));
        },
        onStepResult: (_stepId, partial) => {
          setAccumulated((a) => ({ ...a, ...partial }));
        },
        previous,
      });
      setResult(out);
      setPhase(STATE.DONE);

      // Fire-and-forget: persist to history. Failure must NOT break the UX.
      setHistorySave({ status: 'saving' });
      try {
        const payload = await buildReviewPayload({ result: out, extracted: extractedFile });
        const saved = await saveReview(payload);
        setHistorySave({ status: 'ok', url: saved.url });
      } catch (saveErr) {
        if (saveErr?.status === 503) {
          setHistorySave({ status: 'unconfigured', message: saveErr.message });
        } else {
          setHistorySave({ status: 'error', message: saveErr?.message || 'Save failed' });
        }
      }
    } catch (err) {
      // 401 mid-pipeline (token cleared by api.js) → bounce back to gate
      if (err?.code === 'UNAUTHORIZED') {
        setAuthed(false);
        return;
      }
      setError(err?.message || 'Review failed');
      setPhase(STATE.ERROR);
    }
  }

  function reset() {
    setPhase(STATE.IDLE);
    setExtracted(null);
    setProgress({});
    setAccumulated({});
    setResult(null);
    setError(null);
    setHistorySave(null);
  }

  function showHistory() {
    setPhase(STATE.HISTORY);
  }

  function showPricing() {
    setPhase(STATE.PRICING);
  }

  function retryFromFailure() {
    if (!extracted) return;
    start(extracted, accumulated);
  }

  return (
    <div className="min-h-screen bg-navy-900 text-white">
      <Header onLogout={logout} onHistory={showHistory} onPricing={showPricing} currentPhase={phase} />

      <main className="mx-auto max-w-6xl px-6 py-8">
        <CheckoutToast toast={checkoutToast} onDismiss={() => setCheckoutToast(null)} />
        {phase === STATE.IDLE && <IdleView onExtracted={start} />}

        {phase === STATE.REVIEWING && (
          <ReviewingView extracted={extracted} progress={progress} />
        )}

        {phase === STATE.DONE && result && (
          <DoneView
            extracted={extracted}
            result={result}
            progress={progress}
            historySave={historySave}
            onReset={reset}
          />
        )}

        {phase === STATE.ERROR && (
          <ErrorView
            error={error}
            progress={progress}
            accumulated={accumulated}
            onReset={reset}
            onRetry={retryFromFailure}
          />
        )}

        {phase === STATE.HISTORY && <HistoryView onBack={reset} />}

        {phase === STATE.PRICING && <Pricing onBack={reset} />}
      </main>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
function IdleView({ onExtracted }) {
  return (
    <div className="grid gap-8 md:grid-cols-[1fr_360px]">
      <div className="space-y-6">
        <div>
          <h1 className="mb-2 text-2xl font-semibold">New file review</h1>
          <p className="text-sm text-slate-400">
            Upload a KYC/CDD file or paste the content. RegDefence will run six AI modules and
            return a regulator-ready QA report.
          </p>
        </div>
        <Uploader onExtracted={onExtracted} />
        <SamplePicker onLoaded={onExtracted} />
      </div>

      <aside className="rounded-lg border border-navy-700 bg-navy-800 p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gold">
          What you get back
        </div>
        <ul className="space-y-2 text-sm text-slate-200">
          <li>• Extracted customer facts (JSON)</li>
          <li>• 11-area KYC QA assessment</li>
          <li>• Ranked red flags with severity</li>
          <li>• 8-dimension defensibility verdict</li>
          <li>• Score 0–100 + decision</li>
          <li>• Regulator-ready report</li>
        </ul>
        <div className="mt-5 border-t border-navy-700 pt-4 text-xs text-slate-400">
          Typical review: 60–110 seconds. All processing is server-side via the Anthropic API.
        </div>
      </aside>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
function ReviewingView({ extracted, progress }) {
  return (
    <div className="grid gap-8 md:grid-cols-[1fr_320px]">
      <div>
        <h1 className="mb-2 text-2xl font-semibold">Review in progress</h1>
        <p className="mb-6 text-sm text-slate-400">
          {extracted?.fileName} &middot; {extracted?.text?.length?.toLocaleString()} chars
          {extracted?.pages ? ` · ${extracted.pages} pages` : ''}
        </p>
        <ReviewProgress progress={progress} />
      </div>
      <aside className="rounded-lg border border-navy-700 bg-navy-800 p-5 text-xs text-slate-300">
        <div className="mb-2 font-semibold uppercase tracking-wide text-gold">Extracted preview</div>
        <pre className="prose-mono max-h-96 overflow-auto rounded bg-navy-900 p-3 text-slate-300">
{(extracted?.text || '').slice(0, 1500)}
{extracted?.text?.length > 1500 ? '\n…' : ''}
        </pre>
      </aside>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
function CostStrip({ usage, usageByStep, timings }) {
  const { inputTokens, outputTokens, totalTokens, costUsd } = usage;
  const stepRows = Object.entries(usageByStep || {});
  return (
    <div className="rounded-lg border border-navy-700 bg-navy-800 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-gold">Cost telemetry</div>
        <div className="text-sm text-slate-300">
          <span className="text-2xl font-bold text-white">{formatUsd(costUsd)}</span>
          <span className="ml-3 text-xs text-slate-400">
            {totalTokens.toLocaleString()} tokens · {inputTokens.toLocaleString()} in · {outputTokens.toLocaleString()} out
          </span>
        </div>
      </div>
      {stepRows.length > 0 && (
        <details className="mt-3 text-xs text-slate-300">
          <summary className="cursor-pointer text-slate-400 hover:text-white">Per-step breakdown</summary>
          <div className="mt-2 grid gap-1">
            {stepRows.map(([id, u]) => {
              const ms = timings?.[id] || 0;
              return (
                <div key={id} className="flex items-center justify-between rounded border border-navy-700 bg-navy-900 px-2 py-1">
                  <span className="capitalize text-slate-300">{id}</span>
                  <span className="text-slate-400">
                    {(u.model || '').replace(/^claude-/, '')} · {(u.inputTokens || 0).toLocaleString()}→{(u.outputTokens || 0).toLocaleString()} · {(ms / 1000).toFixed(1)}s
                  </span>
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}

function CheckoutToast({ toast, onDismiss }) {
  if (!toast) return null;
  const palette = {
    success: 'border-emerald-900/50 bg-emerald-900/20 text-emerald-200',
    cancel:  'border-amber-900/50 bg-amber-900/20 text-amber-200',
    error:   'border-red-900/50 bg-red-900/20 text-red-300',
  }[toast.kind] || 'border-navy-700 bg-navy-800 text-slate-200';
  return (
    <div className={`mb-4 flex items-start justify-between gap-4 rounded-lg border p-4 text-sm ${palette}`}>
      <div>
        {toast.kind === 'success' && !toast.email && <span>Payment received. Verifying…</span>}
        {toast.kind === 'success' && toast.email && (
          <span>
            ✓ Payment received from <span className="font-medium">{toast.email}</span>
            {toast.amount ? ` · ${toast.amount}` : ''} · receipt sent by Stripe
          </span>
        )}
        {toast.kind === 'cancel' && <span>{toast.message}</span>}
        {toast.kind === 'error' && <span>Payment verification failed: {toast.message}</span>}
      </div>
      <button onClick={onDismiss} className="text-xs underline opacity-80 hover:opacity-100">Dismiss</button>
    </div>
  );
}

function HistorySaveBadge({ historySave }) {
  if (!historySave) return null;
  if (historySave.status === 'saving') {
    return <span className="text-xs text-slate-400">Saving to history…</span>;
  }
  if (historySave.status === 'ok') {
    return (
      <span className="text-xs text-emerald-300">
        ✓ Saved to history
        {historySave.url && (
          <>
            {' · '}
            <a className="underline hover:text-emerald-200" href={historySave.url} target="_blank" rel="noreferrer">
              Open in Notion ↗
            </a>
          </>
        )}
      </span>
    );
  }
  if (historySave.status === 'unconfigured') {
    return <span className="text-xs text-amber-300">History not configured (Notion env vars missing)</span>;
  }
  return <span className="text-xs text-red-300">History save failed: {historySave.message}</span>;
}

function DoneView({ extracted, result, progress, historySave, onReset }) {
  const score = result.scoring?.score ?? null;
  const band = result.scoring?.band ?? '—';
  const decision = result.scoring?.decision ?? '—';
  const failures = result.scoring?.topThreeFailures || [];
  const totalSeconds = Object.values(progress)
    .map((p) => p.durationMs || 0)
    .reduce((a, b) => a + b, 0) / 1000;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Review complete</h1>
          <p className="text-sm text-slate-400">
            {extracted?.fileName} &middot; {totalSeconds.toFixed(1)}s
          </p>
          <div className="mt-1"><HistorySaveBadge historySave={historySave} /></div>
        </div>
        <button
          onClick={onReset}
          className="rounded-md border border-navy-700 bg-navy-800 px-4 py-2 text-sm hover:border-gold"
        >
          Run another review
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <ScoreCard score={score} band={band} />
        <DecisionCard decision={decision} rationale={result.scoring?.decisionRationale} />
        <FailuresCard failures={failures} />
      </div>

      {result.usage && <CostStrip usage={result.usage} usageByStep={result.usageByStep} timings={result.timings} />}

      <div className="rounded-lg border border-navy-700 bg-navy-800 p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gold">
          Download regulator-ready report
        </div>
        <p className="mb-4 text-xs text-slate-400">
          PDF and Word formats include a branded cover page, score, decision, top failures, and
          all 9 report sections with confidentiality footer.
        </p>
        <DownloadButtons result={result} extracted={extracted} />
      </div>

      {/* Hero output: the regulator-ready report + the score breakdown.
          Everything else is intermediate scaffolding — collapsed by default. */}
      <Section title="Final report">
        <pre className="prose-mono whitespace-pre-wrap rounded-md border border-navy-700 bg-navy-900 p-4 text-slate-200">
{result.reportText}
        </pre>
      </Section>

      <Section title="Score breakdown">
        <ScoreBreakdown breakdown={result.scoring?.breakdown} />
      </Section>

      <IntermediateOutputs result={result} />
    </div>
  );
}

function IntermediateOutputs({ result }) {
  return (
    <details className="group rounded-lg border border-navy-700 bg-navy-800/50 transition hover:border-navy-700/80">
      <summary className="flex cursor-pointer items-center justify-between gap-4 px-5 py-4 text-sm">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gold">
            Intermediate AI outputs
          </div>
          <div className="mt-1 text-xs text-slate-400">
            QA assessment, red flags, defensibility verdict, extracted facts.
            All synthesised into the final report above — open this if you need to audit how the model got there.
          </div>
        </div>
        <span className="select-none text-xs text-slate-400 group-open:hidden">Show ▾</span>
        <span className="hidden select-none text-xs text-slate-400 group-open:inline">Hide ▴</span>
      </summary>
      <div className="space-y-6 border-t border-navy-700 px-5 pb-5 pt-4">
        <Section title="11-area KYC QA assessment">
          <pre className="prose-mono whitespace-pre-wrap rounded-md border border-navy-700 bg-navy-900 p-4 text-slate-200">
{result.qaText}
          </pre>
        </Section>

        <Section title="Red flags (severity-ranked)">
          <pre className="prose-mono whitespace-pre-wrap rounded-md border border-navy-700 bg-navy-900 p-4 text-slate-200">
{result.redFlagsText}
          </pre>
        </Section>

        <Section title="8-dimension defensibility assessment">
          <pre className="prose-mono whitespace-pre-wrap rounded-md border border-navy-700 bg-navy-900 p-4 text-slate-200">
{result.defensibilityText}
          </pre>
        </Section>

        <Section title="Extracted facts (raw JSON)">
          <pre className="prose-mono rounded-md border border-navy-700 bg-navy-900 p-4 text-slate-300">
{JSON.stringify(result.facts, null, 2)}
          </pre>
        </Section>
      </div>
    </details>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gold">{title}</h2>
      {children}
    </section>
  );
}

function ScoreCard({ score, band }) {
  const colour =
    score === null
      ? 'text-slate-400'
      : score >= 75
      ? 'text-emerald-400'
      : score >= 60
      ? 'text-amber-300'
      : score >= 40
      ? 'text-orange-400'
      : 'text-red-400';
  return (
    <div className="rounded-lg border border-navy-700 bg-navy-800 p-5 text-center">
      <div className="text-xs uppercase tracking-wide text-slate-400">Defensibility score</div>
      <div className={`mt-2 text-5xl font-bold ${colour}`}>{score ?? '—'}</div>
      <div className="mt-1 text-sm text-slate-300">{band}</div>
    </div>
  );
}

function DecisionCard({ decision, rationale }) {
  return (
    <div className="rounded-lg border border-navy-700 bg-navy-800 p-5">
      <div className="text-xs uppercase tracking-wide text-slate-400">Decision</div>
      <div className="mt-2 text-xl font-semibold text-gold">{decision}</div>
      {rationale && (
        <p className="mt-2 text-xs leading-relaxed text-slate-300">{rationale}</p>
      )}
    </div>
  );
}

function FailuresCard({ failures }) {
  return (
    <div className="rounded-lg border border-navy-700 bg-navy-800 p-5">
      <div className="text-xs uppercase tracking-wide text-slate-400">Top failures</div>
      {failures.length === 0 ? (
        <div className="mt-2 text-sm text-slate-400">None highlighted.</div>
      ) : (
        <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs text-slate-200">
          {failures.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

function ScoreBreakdown({ breakdown }) {
  if (!breakdown) return <div className="text-sm text-slate-400">No breakdown returned.</div>;
  const rows = Object.entries(breakdown);
  return (
    <div className="grid gap-2 md:grid-cols-3">
      {rows.map(([key, val]) => (
        <div
          key={key}
          className="flex items-center justify-between rounded-md border border-navy-700 bg-navy-900 px-3 py-2"
        >
          <span className="text-sm capitalize text-slate-300">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </span>
          <span className="text-sm font-semibold text-gold">{val}</span>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
function ErrorView({ error, progress, accumulated, onReset, onRetry }) {
  // Find which step failed and how many succeeded before it.
  const failedStepId = Object.entries(progress).find(
    ([, v]) => v?.status === 'error'
  )?.[0];
  const failedStep = PIPELINE_STEPS.find((s) => s.id === failedStepId);
  const completedCount = Object.values(progress).filter((p) => p?.status === 'done').length;
  const accumulatedCount = Object.keys(accumulated).length;
  const canResume = failedStep && accumulatedCount > 0;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-red-900/50 bg-red-900/20 p-6">
        <h2 className="mb-2 text-lg font-semibold text-red-200">Review failed</h2>
        {failedStep && (
          <p className="mb-3 text-sm text-red-300">
            Failed at step {PIPELINE_STEPS.findIndex((s) => s.id === failedStepId) + 1} of {PIPELINE_STEPS.length}:{' '}
            <span className="font-medium">{failedStep.label}</span>
          </p>
        )}
        <p className="text-sm text-red-300">{error}</p>
        {completedCount > 0 && (
          <p className="mt-3 text-xs text-slate-300">
            {completedCount} step{completedCount === 1 ? '' : 's'} completed before the failure — those outputs are cached and won't be re-billed on retry.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        {canResume && (
          <button
            onClick={onRetry}
            className="rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900"
          >
            Retry from {failedStep.label}
          </button>
        )}
        <button
          onClick={onReset}
          className="rounded-md border border-navy-700 bg-navy-800 px-4 py-2 text-sm hover:border-gold"
        >
          Start over
        </button>
      </div>

      <div className="rounded-lg border border-navy-700 bg-navy-800 p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gold">
          Pipeline state
        </div>
        <ReviewProgress progress={progress} />
      </div>
    </div>
  );
}
